import { useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import Seo from "@/components/Seo";
import { CONTACT } from "@/data/site";

function Legal({ title, path, children }) {
  return (
    <div className="bg-navy-900 min-h-screen">
      <Seo title={title} path={path} />
      <section className="pt-40 pb-24">
        <div className="max-w-3xl mx-auto px-6">
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tighter text-white leading-none">{title}</h1>
          <div className="mt-10 space-y-6 text-slate-300 leading-relaxed text-sm [&_h2]:text-white [&_h2]:font-bold [&_h2]:text-xl [&_h2]:mt-8 [&_h2]:mb-2 [&_h2]:font-display">{children}</div>
        </div>
      </section>
    </div>
  );
}

export function AGB() {
  return (
    <Legal title="Allgemeine Geschäftsbedingungen" path="/agb">
      <div data-testid="agb-page">
        <h2>1. Geltungsbereich</h2>
        <p>Diese AGB gelten für alle Vermittlungs- und Beratungsleistungen der {CONTACT.legalName} gegenüber ihren Kunden.</p>
        <h2>2. Leistungsgegenstand</h2>
        <p>Gegenstand ist die Vermittlung von Versicherungs-, Finanzierungs- und Immobilienprodukten sowie die damit verbundene Beratung.</p>
        <h2>3. Vergütung</h2>
        <p>Die Vermittlung von Versicherungen erfolgt in der Regel courtagebasiert über die Produktgeber. Abweichende Vergütungsmodelle werden vorab transparent vereinbart.</p>
        <h2>4. Pflichten des Kunden</h2>
        <p>Der Kunde stellt alle für die Beratung erforderlichen Informationen vollständig und wahrheitsgemäß zur Verfügung.</p>
        <h2>5. Haftung</h2>
        <p>Die Haftung richtet sich nach den gesetzlichen Bestimmungen. Für die Vermittlungstätigkeit besteht eine Vermögensschaden-Haftpflichtversicherung.</p>
        <h2>6. Widerruf</h2>
        <p>Verbrauchern steht ein gesetzliches Widerrufsrecht zu. Einzelheiten ergeben sich aus der jeweiligen Widerrufsbelehrung.</p>
        <h2>7. Schlussbestimmungen</h2>
        <p>Es gilt deutsches Recht. Sollten einzelne Bestimmungen unwirksam sein, bleibt die Wirksamkeit der übrigen unberührt.</p>
        <p className="text-slate-500 mt-8">Hinweis: Muster-Vorlage. Vor dem Live-Gang durch rechtsverbindliche, anwaltlich geprüfte AGB ersetzen.</p>
      </div>
    </Legal>
  );
}

export function Erstinformation() {
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  return (
    <Legal title="Erstinformation (§ 15 VersVermV)" path="/erstinformation">
      <div data-testid="erstinformation-page">
        <div className="mb-8">
          <a data-testid="erstinformation-pdf-link" href={`${BACKEND}/api/legal/erstinformation.pdf`}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-gold-500 text-navy-900 font-bold rounded-sm px-5 py-3 hover:bg-gold-300 transition-colors">
            <Download size={18} /> Erstinformation als PDF herunterladen
          </a>
        </div>

        <p>Gemäß § 15 Versicherungsvermittlungsverordnung (VersVermV) informieren wir Sie über folgende Punkte:</p>

        <h2>Identität und Anschrift</h2>
        <p>Xhevdet Rashica<br />VMF Versicherungsmakler Frankfurt<br />Heddernheimer Landstraße 151<br />
        60439 Frankfurt am Main, Deutschland<br />Telefon: 069 83040769 · E-Mail: info@vm-ffm.de<br />
        Web: <a href="https://www.vm-ffm.de" className="text-gold-500 hover:underline">https://www.vm-ffm.de</a></p>

        <h2>Status / Erlaubnis</h2>
        <p>Wir sind als Versicherungsmakler mit Erlaubnis nach § 34d Abs. 1 Gewerbeordnung (GewO) tätig.<br />
        Zusätzlich bestehen Erlaubnisse nach § 34c Abs. 1 Nr. 1 GewO (Immobilienmakler) und § 34c Abs. 1 Nr. 2 GewO (Darlehensvermittlung).</p>

        <h2>Vermittlerregister</h2>
        <p>Vermittlerregister-Nr.: D-BT4E-ITWM0-28<br />
        Gemeinsame Registerstelle: Deutsche Industrie- und Handelskammer (DIHK), Breite Straße 29, 10178 Berlin<br />
        Web: <a href="https://www.vermittlerregister.info" className="text-gold-500 hover:underline">https://www.vermittlerregister.info</a></p>

        <h2>Zuständige Erlaubnis- und Aufsichtsbehörden</h2>
        <p>Für § 34d GewO: Industrie- und Handelskammer Frankfurt am Main, Börsenplatz 4, 60313 Frankfurt am Main, Telefon: +49 69 2197-0,
        Web: <a href="https://www.frankfurt-main.ihk.de" className="text-gold-500 hover:underline">https://www.frankfurt-main.ihk.de</a><br />
        Für § 34c GewO: Ordnungsamt der Stadt Frankfurt am Main, Kleyerstraße 86, 60326 Frankfurt am Main</p>

        <h2>Offenlegung von Beteiligungen</h2>
        <p>Xhevdet Rashica besitzt weder direkte noch indirekte Beteiligungen von mehr als 10 Prozent an den Stimmrechten oder am Kapital eines Versicherungsunternehmens oder eines Mutterunternehmens eines Versicherungsunternehmens.</p>
        <p>Ebenso besitzt kein Versicherungsunternehmen oder Mutterunternehmen eines Versicherungsunternehmens eine direkte oder indirekte Beteiligung von mehr als 10 Prozent an den Stimmrechten oder am Kapital des Vermittlers.</p>

        <h2>Außergerichtliche Streitbeilegung</h2>
        <p>Versicherungsombudsmann e. V., Postfach 08 06 32, 10006 Berlin,
        Web: <a href="https://www.versicherungsombudsmann.de" className="text-gold-500 hover:underline">https://www.versicherungsombudsmann.de</a></p>
        <p>Ombudsmann Private Kranken- und Pflegeversicherung, Postfach 06 02 22, 10052 Berlin,
        Web: <a href="https://www.pkv-ombudsmann.de" className="text-gold-500 hover:underline">https://www.pkv-ombudsmann.de</a></p>
        <p>Wir sind bereit, an einem Streitbeilegungsverfahren vor den genannten Verbraucherschlichtungsstellen teilzunehmen.</p>
      </div>
    </Legal>
  );
}

export function CookieSettings() {
  const [choice, setChoice] = useState(localStorage.getItem("vmffm_cookie_consent") || "");
  const save = (val) => { localStorage.setItem("vmffm_cookie_consent", val); setChoice(val); toast.success("Einstellungen gespeichert"); };
  return (
    <Legal title="Cookie-Einstellungen" path="/cookie-einstellungen">
      <div data-testid="cookie-settings-page">
        <p>Verwalten Sie hier Ihre Präferenzen. Notwendige Cookies sind für den Betrieb der Website erforderlich und immer aktiv.</p>
        <div className="mt-6 space-y-4">
          <div className="bg-navy-800 border border-white/10 rounded-md p-5 flex items-center justify-between">
            <div><h2 className="!mt-0 !mb-1">Notwendige Cookies</h2><p className="text-slate-400">Session, Sicherheit, Grundfunktionen.</p></div>
            <span className="text-xs bg-gold-500/10 text-gold-500 px-3 py-1 rounded-sm">Immer aktiv</span>
          </div>
          <div className="bg-navy-800 border border-white/10 rounded-md p-5">
            <h2 className="!mt-0 !mb-1">Statistik & Komfort</h2><p className="text-slate-400 mb-4">Helfen uns, die Website zu verbessern (optional).</p>
            <div className="flex gap-3">
              <button data-testid="cookie-set-all" onClick={() => save("all")} className={`px-5 py-2.5 rounded-sm text-sm font-bold transition-colors ${choice === "all" ? "bg-gold-500 text-navy-900" : "border border-white/20 text-white hover:border-gold-500"}`}>Alle akzeptieren</button>
              <button data-testid="cookie-set-essential" onClick={() => save("essential")} className={`px-5 py-2.5 rounded-sm text-sm font-bold transition-colors ${choice === "essential" ? "bg-gold-500 text-navy-900" : "border border-white/20 text-white hover:border-gold-500"}`}>Nur notwendige</button>
            </div>
          </div>
        </div>
        {choice && <p className="mt-4 text-slate-500">Aktuelle Auswahl: <span className="text-gold-500">{choice === "all" ? "Alle Cookies" : "Nur notwendige"}</span></p>}
      </div>
    </Legal>
  );
}
